import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';


import {clearSelection} from 'src/components/data-table-selection/services/data-table-selection-slice';
import {
    useGetActiveSearchTemplate,
    useGetSearchTemplates,
    useSetActiveSearchTemplate,
} from 'src/components/edit-columns/hooks';
import {selectUserShortInfo} from 'src/modules/main/services/user-info-slice';
import {runAccountIdsSearch} from 'src/openapi/admin/apis/RunAccountSearchApi';
import {AccountSortField as AccountSortFields} from 'src/openapi/admin/models/AccountSortField';
import {SearchType} from 'src/openapi/admin/models/SearchType';
import {SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {useAppDispatch} from 'src/store/hooks';

import Loading from '@pb/kit-component-loading';

import {COLUMNS, SELECTION_KEY, SORTING_ERROR} from './constants';
import {selectSelectedColumns, updateSelectedAccountSearchColumns} from './slices/account-search-result-columns.slice';
import {accountSearchResultConferenceUsersActions} from './slices/account-search-result-conference-users.slice';
import {accountSearchResultCreditAlertsUsersActions} from './slices/account-search-result-credit-alerts-users.slice';
import {accountSearchResultCrmIntegrationActions} from './slices/account-search-result-crm-integration.slice';
import {accountSearchResultCrmUsersActions} from './slices/account-search-result-crm-users.slice';
import {accountSearchResultDisabledUsersActions} from './slices/account-search-result-disabled-users.slice';
import {accountSearchResultLicenseActions} from './slices/account-search-result-license.slice';
import {accountSearchResultLicensedUsersActions} from './slices/account-search-result-licensed-users.slice';
import {accountSearchResultMobileUsersActions} from './slices/account-search-result-mobile-users.slice';
import {accountSearchResultNoAccessUsersActions} from './slices/account-search-result-no-access-users.slice';
import {accountSearchResultParentAccountsActions} from './slices/account-search-result-parent-accounts.slice';
import {accountSearchResultPrimaryManagerActions} from './slices/account-search-result-primary-manager.slice';
import {accountSearchResultStateActions, accountSearchResultStateSelector} from './slices/account-search-result-state.slice';
import {
    accountSearchResultActions,
    accountSearchResultDataSelector,
    accountSearchResultResultSelector,
} from './slices/account-search-result.slice';
import {mapColumnIdToAccountSortField} from './utils';

import type {AccountSearchCriteria} from 'src/openapi/admin/models/AccountSearchCriteria';
import type {AccountSearchFiltering} from 'src/openapi/admin/models/AccountSearchFiltering';
import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {UserSearchTemplate} from 'src/openapi/admin/models/UserSearchTemplate';

export type ChildrenProps = {
    adminId: number,
    isLoading: boolean,
    totalCount: number,
    isActiveTemplateLoading: boolean,
    getAllEntityIds: () => Promise<{
        type: string,
        payload: {
            data: string[] | number[],
            success: true,
            error: false,
        } | {
            data: ApiError,
            success: false,
            error: true,
        },
    }>,
    activeSearchTemplate: UserSearchTemplate,
    loadActiveSearchTemplate: () => Promise<void>,
    setActiveSearchTemplate: (actionProps: UserSearchTemplate) => Promise<string | null>,
    templates: UserSearchTemplate[],
    loadTemplates: () => void,
    refreshResults: () => void,
};

type Props = {
    place: string,
    children: (props: ChildrenProps) => React.ReactElement,
    noCriteriaView?: React.ReactElement,
};

export const AccountSearchResultController = ({children, noCriteriaView, place}: Props) => {
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const userShortInfo = useSelector(selectUserShortInfo);
    const selectedColumns = useSelector(selectSelectedColumns);

    const result = useSelector(accountSearchResultResultSelector);
    const {loadingError} = useSelector(accountSearchResultDataSelector);
    const cacheKeys = useSelector(accountSearchResultStateSelector);

    const adminId = useMemo(() => userShortInfo?.userId || 0, [userShortInfo]);

    const refreshResults = useCallback(() => {
        dispatch(clearSelection(SELECTION_KEY));
        dispatch(accountSearchResultStateActions.setFilters({}));
        dispatch(accountSearchResultActions.requireRefresh());

        if (selectedColumns.includes(COLUMNS.PARENT_ACCOUNT_NAME)) {
            dispatch(accountSearchResultParentAccountsActions.requireRefresh());
        }

        if (selectedColumns.includes(COLUMNS.LICENSED_ENABLED_USERS)) {
            dispatch(accountSearchResultLicensedUsersActions.requireRefresh());
        }

        if (selectedColumns.includes(COLUMNS.MOBILE_ENABLED_USERS)) {
            dispatch(accountSearchResultMobileUsersActions.requireRefresh());
        }

        if (selectedColumns.includes(COLUMNS.CRM_ENABLED_USERS)) {
            dispatch(accountSearchResultCrmUsersActions.requireRefresh());
        }

        if (selectedColumns.includes(COLUMNS.DISABLED_NEWSLETTER_USERS)) {
            dispatch(accountSearchResultDisabledUsersActions.requireRefresh());
        }

        if (selectedColumns.includes(COLUMNS.NO_ACCESS_USERS)) {
            dispatch(accountSearchResultNoAccessUsersActions.requireRefresh());
        }

        if (selectedColumns.includes(COLUMNS.CONFERENCE_USERS)) {
            dispatch(accountSearchResultConferenceUsersActions.requireRefresh());
        }

        if (selectedColumns.includes(COLUMNS.CREDIT_ALERTS_ENABLED_USERS)) {
            dispatch(accountSearchResultCreditAlertsUsersActions.requireRefresh());
        }

        if ([
            COLUMNS.SALESFORCE_INTEGRATION,
            COLUMNS.MS_DYNAMICS_INTEGRATION,
            COLUMNS.DEAL_CLOUD_INTEGRATION,
            COLUMNS.HUBSPOT_INTEGRATION,
        ].some(column => selectedColumns.includes(column))) {
            dispatch(accountSearchResultCrmIntegrationActions.requireRefresh());
        }

        if (selectedColumns.includes(COLUMNS.PRIMARY_ACCOUNT_MANAGER)) {
            dispatch(accountSearchResultPrimaryManagerActions.requireRefresh());
        }

        if ([
            COLUMNS.LICENSE_TYPE,
            COLUMNS.LICENSE_START_DATE,
            COLUMNS.LICENSE_END_DATE,
            COLUMNS.LICENSE_SEATS,
        ].some(column => selectedColumns.includes(column))) {
            dispatch(accountSearchResultLicenseActions.requireRefresh());
        }
    }, [selectedColumns, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(accountSearchResultStateActions.clearData());
            dispatch(accountSearchResultActions.clearData());

            if (selectedColumns.includes(COLUMNS.PARENT_ACCOUNT_NAME)) {
                dispatch(accountSearchResultParentAccountsActions.clearData());
            }

            if (selectedColumns.includes(COLUMNS.LICENSED_ENABLED_USERS)) {
                dispatch(accountSearchResultLicensedUsersActions.clearData());
            }

            if (selectedColumns.includes(COLUMNS.MOBILE_ENABLED_USERS)) {
                dispatch(accountSearchResultMobileUsersActions.clearData());
            }

            if (selectedColumns.includes(COLUMNS.CRM_ENABLED_USERS)) {
                dispatch(accountSearchResultCrmUsersActions.clearData());
            }

            if (selectedColumns.includes(COLUMNS.DISABLED_NEWSLETTER_USERS)) {
                dispatch(accountSearchResultDisabledUsersActions.clearData());
            }

            if (selectedColumns.includes(COLUMNS.NO_ACCESS_USERS)) {
                dispatch(accountSearchResultNoAccessUsersActions.clearData());
            }

            if (selectedColumns.includes(COLUMNS.CONFERENCE_USERS)) {
                dispatch(accountSearchResultConferenceUsersActions.clearData());
            }

            if (selectedColumns.includes(COLUMNS.CREDIT_ALERTS_ENABLED_USERS)) {
                dispatch(accountSearchResultCreditAlertsUsersActions.clearData());
            }

            if ([
                COLUMNS.SALESFORCE_INTEGRATION,
                COLUMNS.MS_DYNAMICS_INTEGRATION,
                COLUMNS.DEAL_CLOUD_INTEGRATION,
                COLUMNS.HUBSPOT_INTEGRATION,
            ].some(column => selectedColumns.includes(column))) {
                dispatch(accountSearchResultCrmIntegrationActions.clearData());
            }

            if (selectedColumns.includes(COLUMNS.PRIMARY_ACCOUNT_MANAGER)) {
                dispatch(accountSearchResultPrimaryManagerActions.clearData());
            }

            if ([
                COLUMNS.LICENSE_TYPE,
                COLUMNS.LICENSE_START_DATE,
                COLUMNS.LICENSE_END_DATE,
                COLUMNS.LICENSE_SEATS,
            ].some(column => selectedColumns.includes(column))) {
                dispatch(accountSearchResultLicenseActions.clearData());
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const {
        isLoading: isTemplatesLoading,
        data: templates,
        loadData: loadTemplates,
    } = useGetSearchTemplates({userId: adminId});


    const {
        isLoading: isActiveTemplateLoading,
        data: activeSearchTemplate,
        loadData: loadActiveSearchTemplate,
    } = useGetActiveSearchTemplate({
        adminId,
        searchType: SearchType.ACCOUNT,
        onLoad: (columns, field, direction) => {
            dispatch(updateSelectedAccountSearchColumns(columns.map(col => COLUMNS[col])));
            dispatch(
                accountSearchResultStateActions.setSortCriteria({
                    direction,
                    field: mapColumnIdToAccountSortField(COLUMNS[field]),
                }),
            );
            dispatch(accountSearchResultStateActions.setFilters({}));
            refreshResults();
        },
    });

    const {event: setActiveSearchTemplate} = useSetActiveSearchTemplate({
        userId: adminId,
        onSuccess: loadActiveSearchTemplate,
        place,
    });

    const getAllEntityIds = useCallback(() => apiDispatch(runAccountIdsSearch({
        accountIdsSearchRequest: {
            searchCriteria: cacheKeys.searchCriteria as AccountSearchCriteria,
            filtering: cacheKeys.filters as AccountSearchFiltering,
            sortCriteria: {
                columnId: cacheKeys.sortCriteria.field,
                direction: cacheKeys.sortCriteria.direction,
            },
        },
    })), [cacheKeys, apiDispatch]);


    useEffect(() => {
        if (loadingError?.message === SORTING_ERROR) {
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(accountSearchResultStateActions.setSortCriteria({
                direction: SortDirections.ASC,
                field: AccountSortFields.ACCOUNT_NAME,
            }));
        }
    }, [loadingError, dispatch]);

    const isLoading = useMemo(() => (
        isTemplatesLoading || isActiveTemplateLoading || !result
    ), [isTemplatesLoading, isActiveTemplateLoading, result]);

    if (!adminId || activeSearchTemplate.templateId === -1) {
        return <Loading fast={true}/>;
    }

    if (!Object.keys(cacheKeys.searchCriteria).length) {
        return noCriteriaView || <Loading fast={true}/>;
    }

    return children({
        adminId,
        isLoading,
        totalCount: result?.totalCount || 0,
        isActiveTemplateLoading,
        activeSearchTemplate,
        getAllEntityIds,
        loadActiveSearchTemplate,
        setActiveSearchTemplate,
        templates,
        loadTemplates,
        refreshResults,
    });
};
